/**
 * DE-1545 F-046 — in-flight export visibility.
 *
 * The queue dashboard only counts QUEUEABLE docs (pending/ready), so a document
 * that has reached `currentStep:export` but is not yet delivered vanishes from
 * the lane totals — invisible between "left the last operator step" and "export
 * complete". This pure helper buckets in-flight export docs into a per-job-type
 * busy/failed split so the export lane can render them (busy = system working
 * it; failed = loud, needs attention). Completed/exported and voided docs are
 * excluded — that lifecycle (Export History + Archives + retention) is unchanged.
 */
import { VOIDED_DOC_STATUSES } from "../classification/voided-statuses.js";
/** Terminal export statuses — already delivered, out of the in-flight set. */
const TERMINAL_STATUSES = new Set(["exported", "complete"]);
/** Voided docs (split/merged/deleted) — permanent records, not active work. */
const VOIDED_STATUSES = new Set(VOIDED_DOC_STATUSES);
/** Export statuses that must surface loudly. */
const FAILED_STATUSES = new Set(["failed", "error", "dead_letter"]);
/**
 * Bucket in-flight export docs by job type. Returns a Map keyed by jobTypeId;
 * a lane appears only when it has ≥1 in-flight (busy or failed) doc. `batches`
 * is the count of distinct batchIds among that lane's in-flight docs.
 */
export function resolveExportLaneInflight(docs) {
    const result = new Map();
    const batchSets = new Map();
    for (const d of docs) {
        if (TERMINAL_STATUSES.has(d.status) || VOIDED_STATUSES.has(d.status)) {
            continue;
        }
        let entry = result.get(d.jobTypeId);
        if (!entry) {
            entry = { busy: 0, failed: 0, batches: 0 };
            result.set(d.jobTypeId, entry);
            batchSets.set(d.jobTypeId, new Set());
        }
        if (FAILED_STATUSES.has(d.status)) {
            entry.failed++;
        }
        else {
            entry.busy++;
        }
        batchSets.get(d.jobTypeId).add(d.batchId);
    }
    for (const [jobTypeId, set] of batchSets) {
        result.get(jobTypeId).batches = set.size;
    }
    return result;
}
//# sourceMappingURL=export-lane-inflight.js.map